import { CommonModule } from '@angular/common';
import { Component, computed, ElementRef, Inject, inject, Optional, Signal, signal, ViewChild, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { MeUserAvatarComponent } from '@app/components/common/account/me-user-avatar/me-user-avatar.component';
import { NoResultBlockComponent } from '@app/components/shared/no-result-block/no-result-block.component';
import { CacheService } from '@app/_services/cache.service';
import { SettingsService } from '@app/_services/settings.service';
import { PublicNameService } from '@app/_services/public-name.service';
import { ModalService } from '@app/_services/modal.service';
import { COLLABORATOR_LIMIT } from '@app/_utils/members-model.utils';
import { AlertService } from '@app/_services/alert.service';
import { BlockedUserService } from '@app/_services/blocked-user.service';
import { log } from '@app/_services/logger';
import { CustomBottomSheetRef } from '@app/_services/custom-bottom-sheet.service';
import { MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';

/**
 * Message collaborator picker — derived from Angular CollaboratorComponent.
 *
 * Differences from entity collaborator sheet (matching Flutter ChatCollaboratorPicker):
 * - Title: "New Chat" instead of "Collaborators"
 * - No owner row, no role management
 * - Member tiles show checkboxes instead of role labels
 * - All suggestions shown as full scrollable list (not inline dropdown overlay)
 * - Add user icon in app bar opens email input modal (mobile) or inline input (desktop >750px)
 * - Bottom: "START MESSAGING" button instead of Save/Close bar
 * - Limit banner when 16 reached
 * - No pending changes / save/discard flow
 */
@Component({
    selector: 'app-message-collaborator-picker',
    imports: [
        CommonModule,
        FormsModule,
        SvgComponent,
        MeUserAvatarComponent,
        NoResultBlockComponent,
    ],
    templateUrl: './message-collaborator-picker.component.html',
    styleUrl: './message-collaborator-picker.component.scss'
})
export class MessageCollaboratorPickerComponent {

    readonly cc = inject(CacheService);
    readonly ss = inject(SettingsService);
    readonly publicNameService = inject(PublicNameService);
    readonly alertService = inject(AlertService);
    readonly modalService = inject(ModalService);
    private readonly blockedUserService = inject(BlockedUserService);

    @ViewChild('emailInput') emailInputRef!: ElementRef<HTMLInputElement>;

    // ============ SELECTION STATE ============
    selectedEmails: WritableSignal<string[]> = signal([]);
    private _isLoading = signal(true);

    // ============ INLINE INPUT STATE (desktop only) ============
    isInputActive: WritableSignal<boolean> = signal(false);
    emailInputValue: string = '';

    /**
     * Collaborator emails sorted by display name (matching Flutter _getCollaboratorEmails).
     * Excludes current user. Shows ALL suggestions as a scrollable list.
     */
    collaboratorEmails: Signal<string[]> = computed(() => {
        const allEmails = this.ss.getCollaboratorSuggestions();
        const myEmail = this.cc.user?.email?.toLowerCase();

        const emails = allEmails.filter(email => email.toLowerCase() !== myEmail);

        // Sort alphabetically by display name (matching Flutter)
        emails.sort((a, b) => {
            const nameA = this.publicNameService.getDisplayName(a).toLowerCase();
            const nameB = this.publicNameService.getDisplayName(b).toLowerCase();
            return nameA.localeCompare(nameB);
        });

        return emails;
    });

    /**
     * Whether the collaborator limit has been reached.
     * Max 16 members total (including owner), so max 15 selected.
     */
    isLimitReached: Signal<boolean> = computed(() => {
        return (this.selectedEmails().length + 1) >= COLLABORATOR_LIMIT;
    });

    isLoading: Signal<boolean> = computed(() => this._isLoading());

    constructor(
        @Optional() public dialogRef: MatDialogRef<MessageCollaboratorPickerComponent> | null,
        @Optional() public bottomSheetRef: CustomBottomSheetRef<MessageCollaboratorPickerComponent> | null
    ) {
        this.loadCollaboratorNames();
    }

    /**
     * Preload display names for all suggestions (matches Flutter _loadCollaboratorNames).
     */
    private async loadCollaboratorNames(): Promise<void> {
        const suggestions = this.ss.getCollaboratorSuggestions();
        if (suggestions.length > 0) {
            await this.publicNameService.preload(suggestions);
        }
        this._isLoading.set(false);
    }

    // ============ SELECTION (replaces collaborator's role management) ============

    /**
     * Toggle an email's selection state (matches Flutter _toggleSelection).
     */
    toggleSelection(email: string): void {
        const normalized = email.toLowerCase();

        if (this.selectedEmails().includes(normalized)) {
            this.selectedEmails.update(emails => emails.filter(e => e !== normalized));
        } else {
            if (!this.isLimitReached()) {
                this.selectedEmails.update(emails => [...emails, normalized]);
            }
        }
    }

    /**
     * Check if an email is currently selected.
     */
    isSelected(email: string): boolean {
        return this.selectedEmails().includes(email.toLowerCase());
    }

    /**
     * Get display name for a member tile.
     * Uses fallback name (matches Flutter/Angular collaborator pattern).
     */
    getDisplayName(email: string): string {
        return this.publicNameService.getDisplayName(email, this.publicNameService.fallbackName);
    }

    // ============ ADD COLLABORATOR (modal on mobile, inline input on desktop) ============

    /**
     * Open add-collaborator input. On desktop (>750px) uses inline input; on mobile uses modal.
     */
    async openAddCollaboratorInput(): Promise<void> {
        if (this.isLimitReached()) return;

        if (window.innerWidth > 750) {
            this.toggleInlineInput();
        } else {
            // Lazy load the AddCollaboratorModalComponent (same pattern as collaborators-page)
            const { AddCollaboratorModalComponent } = await import(
                '@app/components/collaborators/add-collaborator-modal/add-collaborator-modal.component'
            );

            const ref = this.modalService.openLevel3(AddCollaboratorModalComponent, { topbar: false });

            this.modalService.getClosedObservable(ref).subscribe(async (result: { email?: string; addedToSuggestions?: boolean } | undefined) => {
                if (result?.email) {
                    const trimmedEmail = result.email.toLowerCase().trim();

                    // Pre-cache display name
                    await this.publicNameService.preload(trimmedEmail);

                    // Auto-select the newly added email (matches Flutter)
                    if (!this.selectedEmails().includes(trimmedEmail) && !this.isLimitReached()) {
                        this.selectedEmails.update(emails => [...emails, trimmedEmail]);
                    }
                }
            });
        }
    }

    toggleInlineInput(): void {
        const newState = !this.isInputActive();
        this.isInputActive.set(newState);
        if (newState) {
            this.emailInputValue = '';
            setTimeout(() => this.emailInputRef?.nativeElement?.focus(), 50);
        } else {
            this.emailInputValue = '';
        }
    }

    async addMemberFromInlineInput(): Promise<void> {
        const emailKey = this.emailInputValue?.toLowerCase().trim();
        if (!emailKey) return;

        if (!this.isValidEmail(emailKey)) {
            await this.alertService.alert(
                this.ss.t('incorrectEmail_title'),
                this.ss.t('incorrectEmail_content'),
                this.ss.t('common_ok')
            );
            return;
        }

        if (emailKey === this.cc.user?.email?.toLowerCase()) {
            await this.alertService.alert(
                this.ss.t('selfInviteDenied_title'),
                this.ss.t('selfInviteDenied_content'),
                this.ss.t('common_ok')
            );
            return;
        }

        if (this.selectedEmails().includes(emailKey)) {
            await this.alertService.alert(
                this.ss.t('memberAlreadyExists_title'),
                this.ss.t('memberAlreadyExists_content'),
            );
            return;
        }

        if (this.isLimitReached()) return;

        this.selectedEmails.update(emails => [...emails, emailKey]);
        this.isInputActive.set(false);
        this.emailInputValue = '';

        await this.publicNameService.preload(emailKey);
        await this.blockedUserService.cacheCollaboratorEmail(emailKey);

        log.info('MessageCollaboratorPicker', 'Collaborator added via inline input', { email: emailKey });
    }

    private isValidEmail(email: string): boolean {
        if (!email || email.length === 0) return false;
        const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return emailRegex.test(email);
    }

    // ============ START MESSAGING (replaces Save/Close) ============

    /**
     * Start messaging with selected emails (matches Flutter _onStartChat).
     */
    startMessaging(): void {
        const selected = this.selectedEmails();
        if (selected.length === 0) return;

        log.info('MessageCollaboratorPicker', 'Starting messaging', { memberCount: selected.length });
        this.close(selected);
    }



    /**
   * Close dialog or bottom sheet (works with both)
   * @param result Optional result to pass when closing
   */
    close(result?: any): void {
        if (this.dialogRef) {
            this.dialogRef.close(result);
        } else if (this.bottomSheetRef) {
            this.bottomSheetRef.dismiss(result);
        }
    }
}
